// Authentication Redux Slice

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authApi from '../../api/authApi';
import StorageUtil from '../../utils/storage';
import { AUTH_STATUS } from '../../constants/app.constants';
import { friendlyLoginError } from '../../utils/authErrors';


// Async Thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await authApi.login(username, password);
      
      // Store tokens
      StorageUtil.setAccessToken(response.access_token);
      StorageUtil.setRefreshToken(response.refresh_token);
      StorageUtil.setUserInfo(response.user);
      
      return {
        user: response.user,
        access_token: response.access_token,
        refresh_token: response.refresh_token,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout();
    } catch (_) {
      // Always clear local session even if API call fails
    }
    StorageUtil.clearAuth();
    return null;
  }
);

// Initial State
const initialState = {
  user: StorageUtil.getUserInfo(),
  accessToken: StorageUtil.getAccessToken(),
  refreshToken: StorageUtil.getRefreshToken(),
  status: AUTH_STATUS.IDLE,
  error: null,
  isAuthenticated: StorageUtil.isAuthenticated(),
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Reset auth state
    resetAuth: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.status = AUTH_STATUS.IDLE;
      state.error = null;
      state.isAuthenticated = false;
      StorageUtil.clearAuth();
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = AUTH_STATUS.LOADING;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = AUTH_STATUS.SUCCESS;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = AUTH_STATUS.ERROR;
        state.error = friendlyLoginError(action.payload);
        state.isAuthenticated = false;
      });

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.status = AUTH_STATUS.LOADING;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.status = AUTH_STATUS.IDLE;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.status = AUTH_STATUS.ERROR;
        state.error = action.payload?.message || 'Logout failed';
      });
  },
});

export const { clearError, resetAuth } = authSlice.actions;
export default authSlice.reducer;
